/* Quiet, one-time editorial entrances. Shopping controls and images are always visible. */
(() => {
  'use strict';
  if (window.__coinyMotionV14) return;
  window.__coinyMotionV14 = true;

  const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  const selector = '.cm-aplus-panel, .cm-v14-editorial-frame, .cm-v14-editorial-copy, [data-cm-v14-reveal]';
  const otherMotion = '[data-cm-reveal], [data-cm-editorial-reveal], .cm-v13-hero, .cm-v8-hero-frame';
  const seen = new WeakSet();
  const pending = new Set();
  const running = new Map();
  const paused = () => Boolean(preference?.matches || connection?.saveData || document.hidden);
  let suspended = false;

  function clearAnimation(element) {
    const animation = running.get(element);
    if (animation) animation.cancel();
    running.delete(element);
    element.classList.remove('cm-v14-motion-entering');
  }

  function enter(element) {
    if (paused() || suspended || typeof element.animate !== 'function') return;
    element.classList.add('cm-v14-motion-entering');
    const animation = element.animate(
      [{ opacity: .88, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' }],
      { duration: 560, easing: 'cubic-bezier(.22,.68,.25,1)' }
    );
    running.set(element, animation);
    const finish = () => {
      if (running.get(element) !== animation) return;
      running.delete(element);
      element.classList.remove('cm-v14-motion-entering');
    };
    animation.onfinish = finish;
    animation.oncancel = finish;
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting || !pending.has(entry.target)) return;
      observer.unobserve(entry.target);
      pending.delete(entry.target);
      entry.target.classList.remove('cm-v14-motion-ready');
      seen.add(entry.target);
      if (entry.target.isConnected) enter(entry.target);
    });
  }, { threshold: .02, rootMargin: '0px 0px -24px 0px' }) : null;

  function sync() {
    const stop = paused() || suspended;
    document.documentElement.setAttribute('data-cm-v14-motion', stop ? 'paused' : 'ready');
    observer?.disconnect();
    if (stop) [...running.keys()].forEach(clearAnimation);
    pending.forEach(element => {
      element.classList.remove('cm-v14-motion-ready');
      if (!element.isConnected) {
        pending.delete(element);
      } else if (element.getBoundingClientRect().top <= window.innerHeight) {
        // Returning from a hidden tab or changing a preference must not animate content already in view.
        pending.delete(element);
        seen.add(element);
      } else if (!stop && observer) {
        element.classList.add('cm-v14-motion-ready');
        observer.observe(element);
      }
    });
  }

  function init(root) {
    if (!observer || !root?.querySelectorAll) return;
    const elements = [...root.querySelectorAll(selector)];
    if (root.matches?.(selector)) elements.unshift(root);
    elements.forEach(element => {
      if (seen.has(element) || pending.has(element) || element.closest(otherMotion)) return;
      // An existing controller, or a selected parent, owns the entire block.
      if (element.querySelector('[data-cm-reveal], [data-cm-editorial-reveal]')) return;
      if (element.parentElement?.closest(selector)) return;
      if (element.getBoundingClientRect().top <= window.innerHeight) {
        seen.add(element);
      } else {
        pending.add(element);
      }
    });
    sync();
  }

  function unload(root) {
    const belongs = element => element === root || root?.contains?.(element) || !element.isConnected;
    pending.forEach(element => {
      if (!belongs(element)) return;
      observer?.unobserve(element);
      pending.delete(element);
      element.classList.remove('cm-v14-motion-ready');
    });
    [...running.keys()].filter(belongs).forEach(clearAnimation);
  }

  if (preference?.addEventListener) preference.addEventListener('change', sync);
  else preference?.addListener?.(sync);
  connection?.addEventListener?.('change', sync);
  document.addEventListener('visibilitychange', sync);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  window.addEventListener('pagehide', () => { suspended = true; sync(); });
  window.addEventListener('pageshow', () => { suspended = false; sync(); });
  sync();
  init(document);
})();
